import React from 'react';
import { Badge } from './ui/badge';
import { CheckCircle2, AlertCircle, Clock } from 'lucide-react';

/**
 * Payment Status badge — at-a-glance deposit + final invoice state for a customer.
 * Variants:
 *   - compact: small kanban-style badge (just ✓ / £ icon + minimal text)
 *   - full: header-style with deposit + final breakdown
 */
export const PaymentStatusBadge = ({ customer, variant = 'full' }) => {
  if (!customer) return null;

  const depositAmount = parseFloat(customer.deposit_amount) || 0;
  const finalAmount = parseFloat(customer.final_bill_amount) || 0;
  const depositPaid = !!customer.deposit_paid || !!customer.deposit_paid_date;
  const finalPaid = !!customer.final_bill_paid || !!customer.final_bill_paid_date;

  // Skip if no money set anywhere
  if (depositAmount === 0 && finalAmount === 0) return null;

  const depositRelevant = depositAmount > 0;
  const finalRelevant = finalAmount > 0;

  const depositOk = !depositRelevant || depositPaid;
  const finalOk = !finalRelevant || finalPaid;
  const allPaid = depositOk && finalOk && (depositRelevant || finalRelevant);

  /* ---- Compact badge for Kanban / list views ---- */
  if (variant === 'compact') {
    if (allPaid) {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] py-0 px-1.5 h-4" data-testid="payment-badge-compact">
          <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Paid
        </Badge>
      );
    }
    // Partial — show what's outstanding
    if (depositRelevant && depositPaid && finalRelevant && !finalPaid) {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] py-0 px-1.5 h-4" data-testid="payment-badge-compact">
          Final due
        </Badge>
      );
    }
    if (depositRelevant && !depositPaid) {
      return (
        <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px] py-0 px-1.5 h-4" data-testid="payment-badge-compact">
          Deposit due
        </Badge>
      );
    }
    return null;
  }

  /* ---- Full / header variant ---- */
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="payment-status-badge">
      {depositRelevant && (
        <Badge
          className={depositPaid
            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
            : 'bg-rose-100 text-rose-700 border-rose-200'}
          data-testid={`deposit-status-${depositPaid ? 'paid' : 'outstanding'}`}
        >
          {depositPaid ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
          Deposit {depositPaid ? 'Paid' : 'Outstanding'}
        </Badge>
      )}
      {finalRelevant && (
        <Badge
          className={finalPaid
            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
            : depositPaid
              ? 'bg-amber-100 text-amber-700 border-amber-200'
              : 'bg-slate-100 text-slate-600 border-slate-200'}
          data-testid={`final-status-${finalPaid ? 'paid' : 'outstanding'}`}
        >
          {finalPaid ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
          Final {finalPaid ? 'Paid' : 'Outstanding'}
        </Badge>
      )}
    </div>
  );
};

export default PaymentStatusBadge;
